import React, { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';

/**
 * Button variants using class-variance-authority for type-safe styling
 */
const buttonVariants = cva(
  // Base styles - more compact and functional
  `
    inline-flex items-center justify-center 
    font-medium text-sm
    transition-all duration-150 ease-out
    focus:outline-none focus:ring-1 focus:ring-offset-1
    dark:focus:ring-offset-neutral-900
    disabled:pointer-events-none disabled:opacity-50
    relative overflow-hidden
    group
  `,
  {
    variants: {
      variant: {
        // Primary - More subtle, less prominent
        primary: `
          bg-primary-600 text-white
          hover:bg-primary-700 active:bg-primary-800
          focus:ring-primary-500
          shadow-sm hover:shadow
          border border-primary-600 hover:border-primary-700
        `,
        
        // Secondary - Subtle gray styling
        secondary: `
          bg-neutral-700 text-neutral-100
          hover:bg-neutral-600 active:bg-neutral-800
          focus:ring-neutral-500
          shadow-sm hover:shadow
          border border-neutral-700 hover:border-neutral-600
        `,
        
        // Outline - Clean outline style
        outline: `
          bg-transparent text-neutral-700 dark:text-neutral-300
          hover:bg-neutral-50 dark:hover:bg-neutral-800
          active:bg-neutral-100 dark:active:bg-neutral-700
          focus:ring-primary-500
          border border-neutral-300 dark:border-neutral-600
          hover:border-neutral-400 dark:hover:border-neutral-500
        `,
        
        // Ghost - Very subtle
        ghost: `
          bg-transparent text-neutral-600 dark:text-neutral-400
          hover:bg-neutral-100 dark:hover:bg-neutral-800
          active:bg-neutral-200 dark:active:bg-neutral-700
          focus:ring-neutral-400
          border border-transparent
        `,
        
        // Gradient - Removed for more professional look
        gradient: `
          bg-gradient-to-r from-primary-600 to-primary-700
          text-white font-medium
          hover:from-primary-700 hover:to-primary-800
          active:from-primary-800 active:to-primary-900
          focus:ring-primary-500
          shadow hover:shadow-md
          border border-transparent
        `,
        
        // Destructive - More subtle
        destructive: `
          bg-error-600 text-white
          hover:bg-error-700 active:bg-error-800
          focus:ring-error-500
          shadow-sm hover:shadow
          border border-error-600 hover:border-error-700
        `,
        
        // Success - More subtle
        success: `
          bg-success-600 text-white
          hover:bg-success-700 active:bg-success-800
          focus:ring-success-500
          shadow-sm hover:shadow
          border border-success-600 hover:border-success-700
        `,
      },
      
      size: {
        xs: 'h-6 px-2 text-xs rounded gap-1',
        sm: 'h-7 px-2.5 text-xs rounded gap-1',
        md: 'h-8 px-3 text-sm rounded gap-1.5',
        lg: 'h-9 px-4 text-sm rounded-md gap-1.5',
        xl: 'h-10 px-5 text-base rounded-md gap-2',
      },
      
      loading: {
        true: 'cursor-wait',
        false: '',
      },
      
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      loading: false,
      fullWidth: false,
    },
  }
);

/**
 * Button size type
 */
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * LoadingSpinner component for button loading state
 */
const LoadingSpinner: React.FC<{ size: ButtonSize }> = ({ size }) => {
  const sizeMap: Record<ButtonSize, string> = {
    xs: 'w-3 h-3',
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-4 h-4',
    xl: 'w-5 h-5',
  };

  return (
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      className={`${sizeMap[size]} border-2 border-current border-t-transparent rounded-full`}
    />
  );
};

/**
 * Button component props
 */
export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'>,
    VariantProps<typeof buttonVariants> {
  /** Content to render inside the button */
  children?: ReactNode;
  /** Icon to show before the text */
  leftIcon?: ReactNode;
  /** Icon to show after the text */
  rightIcon?: ReactNode;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Loading text to show when loading */
  loadingText?: string;
  /** Whether the button should take full width */
  fullWidth?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Button Component
 * A comprehensive button component with multiple variants, sizes, and states
 * Includes smooth animations and accessibility features
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      leftIcon,
      rightIcon,
      loading = false,
      loadingText,
      fullWidth = false,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    
    // Show loading text if provided, otherwise show children
    const content = loading && loadingText ? loadingText : children;
    
    // Show spinner when loading, otherwise show provided icons
    const leftElement = loading ? <LoadingSpinner size={size as ButtonSize} /> : leftIcon;
    const rightElement = loading ? null : rightIcon;

    return (
      <motion.div
        whileHover={!isDisabled ? { scale: 1.02 } : undefined}
        whileTap={!isDisabled ? { scale: 0.98 } : undefined}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30,
        }}
      >
        <button
          ref={ref}
          className={cn(
            buttonVariants({ variant, size, loading, fullWidth }),
            className
          )}
          disabled={isDisabled}
          {...props}
        >
          {/* Shimmer effect overlay for premium variants */}
          {variant === 'gradient' && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.6 }}
            />
          )}
          
          {/* Ripple effect background */}
          <div className="absolute inset-0 rounded-[inherit] overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-white/10 rounded-full scale-0 group-active:scale-150 opacity-0 group-active:opacity-100"
              transition={{ duration: 0.2 }}
            />
          </div>
          
          {/* Content */}
          <div className="relative flex items-center justify-center gap-2">
            {leftElement && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="flex-shrink-0"
              >
                {leftElement}
              </motion.div>
            )}
            
            {content && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: loading ? 0.2 : 0.1 }}
                className="flex-1 truncate"
              >
                {content}
              </motion.span>
            )}
            
            {rightElement && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="flex-shrink-0"
              >
                {rightElement}
              </motion.div>
            )}
          </div>
        </button>
      </motion.div>
    );
  }
);

Button.displayName = 'Button';

/**
 * Button Group Component
 * Groups multiple buttons together with proper spacing and styling
 */
interface ButtonGroupProps {
  children: ReactNode;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
  size?: ButtonSize;
  variant?: VariantProps<typeof buttonVariants>['variant'];
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  className,
  orientation = 'horizontal',
  size,
  variant,
}) => {
  return (
    <div
      className={cn(
        'inline-flex',
        orientation === 'horizontal' ? 'flex-row' : 'flex-col',
        '[&>div>button]:rounded-none',
        '[&>div:first-child>button]:rounded-l-lg',
        '[&>div:last-child>button]:rounded-r-lg',
        orientation === 'vertical' && [
          '[&>div:first-child>button]:rounded-t-lg [&>div:first-child>button]:rounded-l-none',
          '[&>div:last-child>button]:rounded-b-lg [&>div:last-child>button]:rounded-r-none',
        ],
        '[&>div:not(:first-child)>button]:border-l-0',
        orientation === 'vertical' && '[&>div:not(:first-child)>button]:border-t-0 [&>div:not(:first-child)>button]:border-l',
        className
      )}
      role="group"
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === Button) {
          return React.cloneElement(child as React.ReactElement<ButtonProps>, {
            size: size || child.props.size,
            variant: variant || child.props.variant,
          });
        }
        return child;
      })}
    </div>
  );
};

/**
 * Icon Button Component
 * Specialized button for icon-only actions
 */
interface IconButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'> {
  icon: ReactNode;
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, size = 'md', className, ...props }, ref) => {
    const iconSizeMap: Record<ButtonSize, string> = {
      xs: 'w-3 h-3',
      sm: 'w-4 h-4', 
      md: 'w-5 h-5',
      lg: 'w-5 h-5',
      xl: 'w-6 h-6',
    };

    return (
      <Button
        ref={ref}
        size={size}
        className={cn('aspect-square p-0', className)}
        {...props}
      >
        <div className={iconSizeMap[size as ButtonSize]}>
          {icon}
        </div>
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default Button; 